/** Customer detail: who they are, and everything we have done with them. */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { FileText, Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { peso, shortDate } from '../../lib/format';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  Skeleton,
  StatusChip,
  TableWrap,
  Td,
  Th,
  inputClass,
} from '../../components/ui';

interface Activity {
  leads: {
    id: string;
    code: string;
    projectName: string;
    totalContractPrice: string;
    expectedCloseDate: string | null;
    locked: boolean;
    stage: { label: string } | null;
    owner: { name: string } | null;
  }[];
  salesOrders: {
    id: string;
    code: string;
    projectName: string;
    confirmedAt: string;
    totalContractPrice: string;
    billedTotal: string;
    collectedTotal: string;
    outstanding: string;
    contractBalance: string;
  }[];
  billings: {
    id: string;
    code: string;
    type: string;
    status: string;
    billingDate: string;
    dueDate: string;
    total: string;
    settledAmount: string;
    salesOrder: { id: string; code: string; projectName: string };
  }[];
  projects: {
    id: string;
    code: string;
    name: string;
    status: string;
    startDate: string | null;
    targetEndDate: string | null;
    actualEndDate: string | null;
    pm: { name: string } | null;
    _count: { deliverables: number };
    salesOrder: { id: string; code: string };
  }[];
  totals: {
    contracted: string;
    billed: string;
    collected: string;
    dueNow: string;
    contractBalance: string;
  };
}

const BILLING_TONE: Record<string, 'ok' | 'warning' | 'critical' | 'neutral'> = {
  PAID: 'ok',
  PARTIALLY_PAID: 'warning',
  ISSUED: 'warning',
  DRAFT: 'neutral',
  VOID: 'critical',
};

const PROJECT_TONE: Record<string, 'ok' | 'warning' | 'critical' | 'neutral'> = {
  COMPLETED: 'ok',
  ONGOING: 'warning',
  PENDING: 'neutral',
  CANCELLED: 'critical',
};

const pretty = (value: string): string =>
  value.charAt(0) + value.slice(1).toLowerCase().replace(/_/g, ' ');

interface Contact {
  id: string;
  name: string;
  designation: string | null;
  email: string | null;
  mobile: string | null;
  landline: string | null;
  isPrimary: boolean;
}

interface CustomerDetail {
  id: string;
  code: string;
  name: string;
  tin: string | null;
  addressLine: string | null;
  barangay: string | null;
  city: string | null;
  province: string | null;
  zip: string | null;
  isGovernment: boolean;
  status: 'ACTIVE' | 'DISABLED';
  notes: string | null;
  customFields: Record<string, unknown> | null;
  createdAt: string;
  category: { label: string } | null;
  industry: { label: string } | null;
  contacts: Contact[];
  _count: { leads: number; salesOrders: number };
}

type Tab = 'info' | 'contacts' | 'leads' | 'orders' | 'billings' | 'projects';

export function CustomerDetailPage(): JSX.Element {
  const { id } = useParams();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('info');
  const [contactForm, setContactForm] = useState<Partial<Contact> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const customer = useQuery({
    queryKey: ['customer', id],
    queryFn: () => api<{ data: CustomerDetail }>(`/customers/${id}`),
  });

  /** Loaded only once a tab that needs it is opened. */
  const activity = useQuery({
    queryKey: ['customer-activity', id],
    queryFn: () => api<{ data: Activity }>(`/customers/${id}/activity`),
    enabled: tab === 'leads' || tab === 'orders' || tab === 'billings' || tab === 'projects',
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['customer', id] });
    void queryClient.invalidateQueries({ queryKey: ['customers'] });
  };

  const saveContact = useMutation({
    mutationFn: (contact: Partial<Contact>) =>
      contact.id
        ? api(`/customers/contacts/${contact.id}`, { method: 'PATCH', body: contact })
        : api(`/customers/${id}/contacts`, { method: 'POST', body: contact }),
    onSuccess: () => {
      setError(null);
      setContactForm(null);
      invalidate();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not save the contact.'),
  });

  const removeContact = useMutation({
    mutationFn: (contactId: string) =>
      api(`/customers/contacts/${contactId}`, { method: 'DELETE' }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not remove the contact.'),
  });

  const setStatus = useMutation({
    mutationFn: (status: 'ACTIVE' | 'DISABLED') =>
      api(`/customers/${id}/status`, { method: 'POST', body: { status } }),
    onSuccess: invalidate,
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not change the status.'),
  });

  if (customer.isLoading) return <Skeleton className="h-96 w-full" />;
  const row = customer.data?.data;
  if (!row) return <Alert tone="critical">Customer not found.</Alert>;

  const tabs: { key: Tab; label: string; count?: number }[] = [
    { key: 'info', label: 'Info' },
    { key: 'contacts', label: 'Contacts', count: row.contacts.length },
    { key: 'leads', label: 'Leads', count: row._count.leads },
    { key: 'orders', label: 'Sales Orders', count: row._count.salesOrders },
    {
      key: 'billings',
      label: 'Billings & Balance',
      count: activity.data?.data.billings.length,
    },
    { key: 'projects', label: 'Projects', count: activity.data?.data.projects.length },
  ];

  const address = [row.addressLine, row.barangay, row.city, row.province, row.zip]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold text-navy dark:text-white">{row.name}</h1>
            <StatusChip tone={row.status === 'ACTIVE' ? 'ok' : 'neutral'}>
              {row.status === 'ACTIVE' ? 'Active' : 'Disabled'}
            </StatusChip>
            {row.isGovernment && <StatusChip tone="info">Government</StatusChip>}
          </div>
          <p className="font-mono text-xs text-slate-500">
            {row.code} · added {shortDate(row.createdAt)}
          </p>
        </div>
        <div className="flex gap-2">
          {can('customers.update') && (
            <Link to={`/customers/${row.id}/edit`}>
              <Button variant="secondary">
                <Pencil aria-hidden="true" className="h-4 w-4" /> Edit
              </Button>
            </Link>
          )}
          {can('customers.disable') && (
            <Button
              variant={row.status === 'ACTIVE' ? 'secondary' : 'primary'}
              onClick={() => setStatus.mutate(row.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE')}
            >
              {row.status === 'ACTIVE' ? 'Disable' : 'Enable'}
            </Button>
          )}
        </div>
      </header>

      {error && <Alert tone="critical">{error}</Alert>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((item) => (
          <button
            key={item.key}
            onClick={() => setTab(item.key)}
            className={`rounded-xl px-3 py-2 text-sm font-medium ${
              tab === item.key
                ? 'bg-navy text-white'
                : 'border border-slate-200 bg-white text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300'
            }`}
          >
            {item.label}
            {item.count !== undefined && <span className="ml-1 opacity-70">({item.count})</span>}
          </button>
        ))}
      </div>

      {tab === 'info' && (
        <Card title="Details">
          <dl className="grid gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">TIN</dt>
              <dd className="tabular">{row.tin ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Category</dt>
              <dd>{row.category?.label ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Industry</dt>
              <dd>{row.industry?.label ?? '—'}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-xs uppercase tracking-wide text-slate-500">Address</dt>
              <dd>{address || '—'}</dd>
            </div>
            {Object.entries(row.customFields ?? {}).length > 0 && (
              <div className="sm:col-span-2">
                <dt className="text-xs uppercase tracking-wide text-slate-500">Additional</dt>
                <dd className="mt-1 grid gap-1 text-sm">
                  {Object.entries(row.customFields ?? {}).map(([key, value]) => (
                    <span key={key}>
                      <span className="text-slate-500">{key}:</span> {String(value)}
                    </span>
                  ))}
                </dd>
              </div>
            )}
            {row.notes && (
              <div className="sm:col-span-2">
                <dt className="text-xs uppercase tracking-wide text-slate-500">Notes</dt>
                <dd className="whitespace-pre-wrap">{row.notes}</dd>
              </div>
            )}
          </dl>
        </Card>
      )}

      {tab === 'contacts' && (
        <Card
          title="Contacts"
          description="One contact is the primary one; leads name a person from this list."
          actions={
            can('customers.update') && (
              <Button size="sm" onClick={() => setContactForm({})}>
                <Plus aria-hidden="true" className="h-4 w-4" /> Add contact
              </Button>
            )
          }
        >
          {row.contacts.length === 0 ? (
            <EmptyState
              title="No contacts yet"
              description="Add the person you deal with, so leads and statements can name them."
            />
          ) : (
            <TableWrap>
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Designation</Th>
                  <Th>Email</Th>
                  <Th>Phone</Th>
                  <Th align="right">Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {row.contacts.map((contact) => (
                  <tr key={contact.id}>
                    <Td>
                      <span className="inline-flex items-center gap-1.5 font-medium">
                        {contact.isPrimary && (
                          <Star aria-hidden="true" className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                        )}
                        {contact.name}
                      </span>
                    </Td>
                    <Td>{contact.designation ?? '—'}</Td>
                    <Td>{contact.email ?? '—'}</Td>
                    <Td>{contact.mobile ?? contact.landline ?? '—'}</Td>
                    <Td align="right">
                      {can('customers.update') && (
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setContactForm(contact)}>
                            <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeContact.mutate(contact.id)}
                          >
                            <Trash2 aria-hidden="true" className="h-3.5 w-3.5 text-red-600" />
                          </Button>
                        </div>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Card>
      )}

      {tab !== 'info' && tab !== 'contacts' && activity.isLoading && (
        <Skeleton className="h-64 w-full" />
      )}

      {tab === 'leads' && activity.data && (
        <Card title="Leads">
          {activity.data.data.leads.length === 0 ? (
            <EmptyState
              title="No leads yet"
              description="Create a lead to record a proposal for this customer."
            />
          ) : (
            <TableWrap>
              <thead>
                <tr>
                  <Th>Lead</Th>
                  <Th>Stage</Th>
                  <Th>Owner</Th>
                  <Th>Expected close</Th>
                  <Th align="right">Contract</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {activity.data.data.leads.map((lead) => (
                  <tr key={lead.id}>
                    <Td>
                      <Link to={`/leads/${lead.id}`} className="font-medium text-brand hover:underline">
                        {lead.code}
                      </Link>
                      <p className="text-xs text-slate-500">{lead.projectName}</p>
                    </Td>
                    <Td>
                      <StatusChip tone={lead.locked ? 'ok' : 'neutral'}>
                        {lead.locked ? 'Confirmed' : (lead.stage?.label ?? '—')}
                      </StatusChip>
                    </Td>
                    <Td>{lead.owner?.name ?? '—'}</Td>
                    <Td>{lead.expectedCloseDate ? shortDate(lead.expectedCloseDate) : '—'}</Td>
                    <Td align="right" className="tabular">
                      {peso(lead.totalContractPrice)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Card>
      )}

      {tab === 'orders' && activity.data && (
        <Card title="Sales orders">
          {activity.data.data.salesOrders.length === 0 ? (
            <EmptyState
              title="No sales orders yet"
              description="Confirm a lead and its sales order appears here."
            />
          ) : (
            <TableWrap>
              <thead>
                <tr>
                  <Th>Order</Th>
                  <Th>Confirmed</Th>
                  <Th align="right">Contract</Th>
                  <Th align="right">Billed</Th>
                  <Th align="right">Collected</Th>
                  <Th align="right">Balance</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {activity.data.data.salesOrders.map((order) => (
                  <tr key={order.id}>
                    <Td>
                      <Link
                        to={`/sales-orders/${order.id}`}
                        className="font-medium text-brand hover:underline"
                      >
                        {order.code}
                      </Link>
                      <p className="text-xs text-slate-500">{order.projectName}</p>
                    </Td>
                    <Td>{shortDate(order.confirmedAt)}</Td>
                    <Td align="right" className="tabular">{peso(order.totalContractPrice)}</Td>
                    <Td align="right" className="tabular">{peso(order.billedTotal)}</Td>
                    <Td align="right" className="tabular">{peso(order.collectedTotal)}</Td>
                    <Td align="right" className="tabular font-semibold">
                      {peso(order.contractBalance)}
                      {Number(order.outstanding) > 0 && (
                        <span className="block text-xs font-normal text-slate-500">
                          {peso(order.outstanding)} due now
                        </span>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Card>
      )}

      {tab === 'billings' && activity.data && (
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <p className="text-xs uppercase tracking-wide text-slate-500">Contracted</p>
              <p className="tabular text-lg font-bold">
                {peso(activity.data.data.totals.contracted)}
              </p>
            </Card>
            <Card>
              <p className="text-xs uppercase tracking-wide text-slate-500">Billed</p>
              <p className="tabular text-lg font-bold">{peso(activity.data.data.totals.billed)}</p>
            </Card>
            <Card>
              <p className="text-xs uppercase tracking-wide text-slate-500">Collected</p>
              <p className="tabular text-lg font-bold">
                {peso(activity.data.data.totals.collected)}
              </p>
            </Card>
            <Card className="bg-navy text-white dark:bg-navy">
              <p className="text-xs uppercase tracking-wide text-white/70">Balance</p>
              <p className="tabular text-lg font-bold">
                {peso(activity.data.data.totals.contractBalance)}
              </p>
              {Number(activity.data.data.totals.dueNow) > 0 && (
                <p className="text-xs text-white/70">
                  {peso(activity.data.data.totals.dueNow)} due now
                </p>
              )}
            </Card>
          </div>

          <Card
            title="Statements"
            actions={
              can('billing.view') && activity.data.data.billings.length > 0 ? (
                <Link to={`/billing/customers/${id}/statement`}>
                  <Button size="sm" variant="secondary">
                    <FileText aria-hidden="true" className="h-4 w-4" /> Statement of account
                  </Button>
                </Link>
              ) : undefined
            }
          >
            {activity.data.data.billings.length === 0 ? (
              <EmptyState
                title="Nothing billed yet"
                description="Statements raised against this customer's orders appear here."
              />
            ) : (
              <TableWrap>
                <thead>
                  <tr>
                    <Th>Statement</Th>
                    <Th>Order</Th>
                    <Th>Dated</Th>
                    <Th>Due</Th>
                    <Th align="right">Amount</Th>
                    <Th align="right">Paid</Th>
                    <Th>Status</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {activity.data.data.billings.map((billing) => (
                    <tr key={billing.id}>
                      <Td>
                        <Link
                          to={`/billing/${billing.id}`}
                          className="font-mono text-xs text-brand hover:underline"
                        >
                          {billing.code}
                        </Link>
                        <p className="text-xs text-slate-500">{pretty(billing.type)}</p>
                      </Td>
                      <Td>
                        <p className="text-xs">{billing.salesOrder.code}</p>
                        <p className="text-xs text-slate-500">{billing.salesOrder.projectName}</p>
                      </Td>
                      <Td>{shortDate(billing.billingDate)}</Td>
                      <Td>{shortDate(billing.dueDate)}</Td>
                      <Td align="right" className="tabular">{peso(billing.total)}</Td>
                      <Td align="right" className="tabular">{peso(billing.settledAmount)}</Td>
                      <Td>
                        <StatusChip tone={BILLING_TONE[billing.status] ?? 'neutral'}>
                          {pretty(billing.status)}
                        </StatusChip>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </TableWrap>
            )}
          </Card>
        </div>
      )}

      {tab === 'projects' && activity.data && (
        <Card title="Projects">
          {activity.data.data.projects.length === 0 ? (
            <EmptyState
              title="No projects yet"
              description="A project is created when a lead is confirmed."
            />
          ) : (
            <TableWrap>
              <thead>
                <tr>
                  <Th>Project</Th>
                  <Th>Order</Th>
                  <Th>Manager</Th>
                  <Th align="right">Cards</Th>
                  <Th>Dates</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {activity.data.data.projects.map((project) => (
                  <tr key={project.id}>
                    <Td>
                      <Link
                        to={`/projects/${project.id}`}
                        className="font-medium text-brand hover:underline"
                      >
                        {project.code}
                      </Link>
                      <p className="text-xs text-slate-500">{project.name}</p>
                    </Td>
                    <Td>
                      <span className="text-xs">{project.salesOrder.code}</span>
                    </Td>
                    <Td>{project.pm?.name ?? '—'}</Td>
                    <Td align="right">{project._count.deliverables}</Td>
                    <Td>
                      <p className="text-xs">
                        {project.startDate ? shortDate(project.startDate) : '—'} →{' '}
                        {project.actualEndDate
                          ? shortDate(project.actualEndDate)
                          : project.targetEndDate
                            ? shortDate(project.targetEndDate)
                            : '—'}
                      </p>
                      {project.actualEndDate && (
                        <p className="text-xs text-slate-500">completed</p>
                      )}
                    </Td>
                    <Td>
                      <StatusChip tone={PROJECT_TONE[project.status] ?? 'neutral'}>
                        {pretty(project.status)}
                      </StatusChip>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Card>
      )}

      {contactForm && (
        <Modal
          title={contactForm.id ? 'Edit contact' : 'Add contact'}
          onClose={() => setContactForm(null)}
        >
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              saveContact.mutate(contactForm);
            }}
          >
            <Field label="Name" required>
              <input
                className={inputClass}
                required
                value={contactForm.name ?? ''}
                onChange={(event) => setContactForm({ ...contactForm, name: event.target.value })}
              />
            </Field>
            <Field label="Designation">
              <input
                className={inputClass}
                value={contactForm.designation ?? ''}
                onChange={(event) =>
                  setContactForm({ ...contactForm, designation: event.target.value })
                }
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Email">
                <input
                  className={inputClass}
                  type="email"
                  value={contactForm.email ?? ''}
                  onChange={(event) => setContactForm({ ...contactForm, email: event.target.value })}
                />
              </Field>
              <Field label="Mobile">
                <input
                  className={inputClass}
                  value={contactForm.mobile ?? ''}
                  onChange={(event) => setContactForm({ ...contactForm, mobile: event.target.value })}
                />
              </Field>
            </div>
            <Field label="Landline">
              <input
                className={inputClass}
                value={contactForm.landline ?? ''}
                onChange={(event) => setContactForm({ ...contactForm, landline: event.target.value })}
              />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-navy focus:ring-brand"
                checked={contactForm.isPrimary ?? false}
                onChange={(event) =>
                  setContactForm({ ...contactForm, isPrimary: event.target.checked })
                }
              />
              Primary contact
            </label>
            <div className="flex gap-2">
              <Button type="submit" busy={saveContact.isPending}>
                Save contact
              </Button>
              <Button type="button" variant="secondary" onClick={() => setContactForm(null)}>
                Cancel
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
